import { useEffect } from 'react';
import { usePageSEO } from '@/hooks/usePageSEO';
import FadeInSection from '@/components/base/FadeInSection';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Hero from './components/Hero';
import ConnaitreQuartier from './components/ConnaitreQuartier';
import ProfilToitures from './components/ProfilToitures';
import ServicesQuartier from './components/ServicesQuartier';
import PrixTypiques from './components/PrixTypiques';
import ProjetsRecents from './components/ProjetsRecents';
import TemoignageLocal from './components/TemoignageLocal';
import ZonesProches from './components/ZonesProches';
import CTAFinal from './components/CTAFinal';

function setMeta(name: string, content: string) {
  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function removeOldLdJson() {
  const existing = document.querySelectorAll('script[type="application/ld+json"]');
  existing.forEach((el) => {
    if (el.id === 'charny-ld') el.remove();
  });
}

const breadcrumbItems = [
  { label: 'Accueil', href: '/' },
  { label: 'Secteurs', href: null },
  { label: 'Charny', href: '/secteurs/charny' },
];

export default function CharnyPage() {
  usePageSEO({
    title: "Couvreur à Charny | Lévis | Maisons historiques & Duplex | RBQ",
    description: "Couvreur spécialisé à Charny, Lévis. Réfection toitures maisons historiques en brique et duplex anciens de la rue Principale. Soumission gratuite sous 2h. Couvreurs partenaires RBQ. 418-476-9950.",
    urlPath: "/secteurs/charny",
    keywords: "couvreur Charny, toiture Charny, réfection Charny, bardeau Lévis, maison historique Charny, duplex Charny, rue Principale, soumission",
    ogTitle: "Couvreur à Charny — Réfection toitures historiques",
    ogDescription: "Expert en réfection de toitures pour maisons en pierre et duplex de la rue Principale à Charny. Plus de 50 projets réalisés dans le secteur.",
    ogImage: 'https://couvreur-levis.com/og/og-charny.jpg',
    ogImageAlt: 'Couvreur à Charny, Lévis - Réfection toitures historiques et duplex',
    geoPosition: "46.7250;-71.2667",
    geoPlacename: "Charny, Lévis, QC",
    geoRegion: "CA-QC",
  });

  useEffect(() => {
    document.title = "Couvreur à Charny | Lévis | Maisons historiques & Duplex | RBQ";
    setMeta('description', "Couvreur spécialisé à Charny, Lévis. Réfection toitures maisons historiques en brique et duplex anciens de la rue Principale. Soumission gratuite sous 2h. Couvreurs partenaires RBQ. 418-476-9950.");
    setMeta('keywords', 'couvreur Charny, toiture Charny, réfection Charny, bardeau Lévis, maison historique Charny, duplex Charny, rue Principale, soumission');
    setMeta('geo.position', '46.7250;-71.2667');
    setMeta('geo.placename', 'Charny, Lévis, QC');
    setMeta('geo.region', 'CA-QC');

    removeOldLdJson();

    const ld = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'LocalBusiness',
          name: 'Couvreur-Lévis.com — Charny',
          url: 'https://couvreur-levis.com/secteurs/charny',
          telephone: '+1-418-476-9950',
          email: 'contact@couvreur-levis.com',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Charny',
            addressRegion: 'QC',
            addressCountry: 'CA',
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 46.7196,
            longitude: -71.2782,
          },
          openingHoursSpecification: [
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
              opens: '07:00',
              closes: '18:00',
            },
          ],
          priceRange: '$$',
          areaServed: {
            '@type': 'Place',
            name: 'Charny, Lévis',
          },
          description:
            "Couvreur spécialisé à Charny, Lévis. Réfection toitures maisons historiques en brique et duplex anciens de la rue Principale. Soumission gratuite sous 2h. Couvreurs partenaires RBQ. 418-476-9950.",
          image: 'https://couvreur-levis.com/og/og-charny.jpg',
          hasMap: 'https://www.google.com/maps?q=Charny,L%C3%A9vis,QC',
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbItems.map((item, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            name: item.label,
            item: item.href ? `https://couvreur-levis.com${item.href}` : undefined,
          })),
        },
        {
          '@type': 'Organization',
          name: 'Couvreur-Lévis.com',
          url: 'https://couvreur-levis.com',
          logo: 'https://couvreur-levis.com/og-default.jpg',
          description: 'Plateforme de mise en relation avec des couvreurs certifiés à Lévis et environs.',
          telephone: '+1-418-476-9950',
          email: 'contact@couvreur-levis.com',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Lévis',
            addressRegion: 'QC',
            addressCountry: 'CA',
          },
        },
        {
          '@type': 'Service',
          serviceType: 'Services de toiture',
          provider: { '@type': 'LocalBusiness', '@id': 'https://couvreur-levis.com/#business' },
          areaServed: {
            '@type': 'Place',
            name: 'Charny',
            containedInPlace: { '@type': 'City', name: 'Lévis' },
          },
          description: "Couvreurs partenaires à Charny, Lévis. Toiture, réfection, réparation, urgence. Soumission gratuite en 2h.",
        },
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'charny-ld';
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);

    return () => {
      removeOldLdJson();
    };
  }, []);

  return (
    <div className="font-inter min-h-screen bg-white">
      <Header />
      <main>
        <Hero />
        <FadeInSection delay={100}><ConnaitreQuartier /></FadeInSection>
        <FadeInSection delay={200}><ProfilToitures /></FadeInSection>
        <FadeInSection delay={300}><ServicesQuartier /></FadeInSection>
        <FadeInSection delay={400}><PrixTypiques /></FadeInSection>
        <FadeInSection delay={500}><ProjetsRecents /></FadeInSection>
        <FadeInSection delay={600}><TemoignageLocal /></FadeInSection>
        <FadeInSection delay={700}><ZonesProches /></FadeInSection>
        <FadeInSection delay={800}><CTAFinal /></FadeInSection>
      </main>
      <Footer />
    </div>
  );
}